import React from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const ServicesStep = ({ formData, updateFormData, errors }) => {
  const serviceCategories = [
    {
      id: 'solar',
      name: 'Zonnepanelen',
      icon: 'Sun',
      description: 'Installatie en onderhoud van zonnepaneel systemen',
      fields: ['solarCapacity', 'solarBrands']
    },
    {
      id: 'heatpump',
      name: 'Warmtepompen',
      icon: 'Thermometer',
      description: 'Lucht-water en bodem warmtepompen',
      fields: ['heatpumpTypes', 'heatpumpBrands']
    },
    {
      id: 'battery',
      name: 'Thuisbatterijen',
      icon: 'Battery',
      description: 'Energieopslag systemen voor woningen',
      fields: ['batteryCapacity', 'batteryBrands']
    },
    {
      id: 'watersoftener',
      name: 'Waterontharders',
      icon: 'Droplets',
      description: 'Waterbehandeling en onthardings systemen',
      fields: ['waterSoftenerTypes']
    },
    {
      id: 'insulation',
      name: 'Isolatie',
      icon: 'Home',
      description: 'Dak-, muur- en vloerisolatie',
      fields: ['insulationTypes']
    },
    {
      id: 'ventilation',
      name: 'Ventilatie',
      icon: 'Wind',
      description: 'Mechanische ventilatie systemen',
      fields: ['ventilationTypes']
    }
  ];

  const handleServiceToggle = (serviceId) => {
    const currentServices = formData?.selectedServices || [];
    const updatedServices = currentServices?.includes(serviceId)
      ? currentServices?.filter(id => id !== serviceId)
      : [...currentServices, serviceId];
    
    updateFormData({ selectedServices: updatedServices });
  };

  const handleServiceDetailChange = (field, value) => {
    updateFormData({ [field]: value });
  };

  const isServiceSelected = (serviceId) => {
    return (formData?.selectedServices || [])?.includes(serviceId);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">
          Diensten & Specialisaties
        </h2>
        <p className="text-muted-foreground">
          Selecteer de duurzaamheidsdiensten die uw bedrijf aanbiedt. U kunt meerdere categorieën kiezen.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {serviceCategories?.map((category) => (
          <div
            key={category?.id}
            className={`border rounded-lg p-4 transition-micro cursor-pointer ${
              isServiceSelected(category?.id)
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
            }`}
            onClick={() => handleServiceToggle(category?.id)}
          >
            <div className="flex items-start space-x-3">
              <Checkbox
                checked={isServiceSelected(category?.id)}
                onChange={() => handleServiceToggle(category?.id)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <Icon name={category?.icon} size={20} color="var(--color-primary)" />
                  <h3 className="font-heading font-medium text-foreground">
                    {category?.name}
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  {category?.description}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Conditional Fields for Selected Services */}
      {(formData?.selectedServices || [])?.length > 0 && (
        <div className="space-y-6 pt-6 border-t border-border">
          <h3 className="text-lg font-heading font-medium text-foreground">
            Service Details
          </h3>

          {isServiceSelected('solar') && (
            <div className="bg-muted/30 rounded-lg p-4 space-y-4">
              <h4 className="font-medium text-foreground flex items-center space-x-2">
                <Icon name="Sun" size={16} />
                <span>Zonnepanelen Details</span>
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Maximale capaciteit (kWp)"
                  type="number"
                  placeholder="50"
                  value={formData?.solarCapacity || ''}
                  onChange={(e) => handleServiceDetailChange('solarCapacity', e?.target?.value)}
                />
                <Input
                  label="Merken waarmee u werkt"
                  type="text"
                  placeholder="SunPower, LG, Panasonic"
                  value={formData?.solarBrands || ''}
                  onChange={(e) => handleServiceDetailChange('solarBrands', e?.target?.value)}
                />
              </div>
            </div>
          )}

          {isServiceSelected('heatpump') && (
            <div className="bg-muted/30 rounded-lg p-4 space-y-4">
              <h4 className="font-medium text-foreground flex items-center space-x-2">
                <Icon name="Thermometer" size={16} />
                <span>Warmtepomp Details</span>
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Type warmtepompen"
                  type="text"
                  placeholder="Lucht-water, Bodem-water"
                  value={formData?.heatpumpTypes || ''}
                  onChange={(e) => handleServiceDetailChange('heatpumpTypes', e?.target?.value)}
                />
                <Input
                  label="Merken waarmee u werkt"
                  type="text"
                  placeholder="Daikin, Mitsubishi, Vaillant"
                  value={formData?.heatpumpBrands || ''}
                  onChange={(e) => handleServiceDetailChange('heatpumpBrands', e?.target?.value)}
                />
              </div>
            </div>
          )}

          {isServiceSelected('battery') && (
            <div className="bg-muted/30 rounded-lg p-4 space-y-4">
              <h4 className="font-medium text-foreground flex items-center space-x-2">
                <Icon name="Battery" size={16} />
                <span>Thuisbatterij Details</span>
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Maximale capaciteit (kWh)"
                  type="number"
                  placeholder="20"
                  value={formData?.batteryCapacity || ''}
                  onChange={(e) => handleServiceDetailChange('batteryCapacity', e?.target?.value)}
                />
                <Input
                  label="Merken waarmee u werkt"
                  type="text"
                  placeholder="Tesla, Enphase, SolarEdge"
                  value={formData?.batteryBrands || ''}
                  onChange={(e) => handleServiceDetailChange('batteryBrands', e?.target?.value)}
                />
              </div>
            </div>
          )}

          <div className="space-y-4">
            <Input
              label="Bedrijfsbeschrijving"
              type="text"
              placeholder="Beschrijf uw bedrijf en ervaring in de duurzaamheidssector..."
              value={formData?.companyDescription || ''}
              onChange={(e) => handleServiceDetailChange('companyDescription', e?.target?.value)}
              error={errors?.companyDescription}
            />
            
            <Input
              label="Aantal medewerkers"
              type="number"
              placeholder="5"
              min="1"
              value={formData?.employeeCount || ''}
              onChange={(e) => handleServiceDetailChange('employeeCount', e?.target?.value)}
            />
          </div>
        </div>
      )}
      {errors?.selectedServices && (
        <div className="text-error text-sm">
          {errors?.selectedServices}
        </div>
      )}
    </div>
  );
};

export default ServicesStep;